import { Form, Formik } from "formik";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import { bool } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import MainContainer from "pages/_components/MainContainer";
// eslint-disable-next-line import/no-named-as-default
import ResponsiveHeader from "pages/_components/header";
import CustomText from "pages/_components/CustomText/CustomText";
import * as configUtils from "util/config";
import { push } from "connected-react-router";
import { useDispatch, useSelector } from "react-redux";
import { actions as comexActions } from "reducers/comex";
import ComexStepper from "../_components/ComexStepper";
// eslint-disable-next-line import/no-named-as-default
import useDDJJEnabledGoodsStatement from "./hooks/useDDJJEnabledGoodsStatement";
import DDJJItem from "../_components/DDJJItem";
import DDJJSubOptions from "../_components/DDJJSubOptions";

const FORM_ID = "comex.ddjj.goods";
const StepDDJJGoods = ({ isDesktop }) => {
    const dispatch = useDispatch();

    const currentRow = useRef(4);
    const { enabledStatements } = useDDJJEnabledGoodsStatement();
    const ddjjStatements = Array.from({ length: 8 }, (_, i) => `II${i + 1}`);
    const { comexTransfer = {}, comexId = null } = useSelector((state) => state.comex);
    const { ddjjGoods = {} } = comexTransfer;
    const [formValues, setFormValues] = useState({});
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);

    useEffect(() => {
        const isSectionValid = (vKey) => {
            if (!enabledStatements[vKey]) {
                return true;
            }

            const subOptions = configUtils.getArray(`${FORM_ID}.${vKey}.options`);
            if (!subOptions?.length) {
                return true;
            }

            const valuesObj = formValues?.[vKey] || {};
            return Object.values(valuesObj).some((v) => (typeof v === "object" ? v?.checked : v));
        };

        const isComplete = ddjjStatements.every(isSectionValid);
        setIsSaveEnabled(isComplete);
    }, [formValues, enabledStatements]);

    const renderSubOptionsWithRows = (vKey, startingRow) => {
        const configPath = `${FORM_ID}.${vKey}.options`;
        const subOptions = configUtils.getArray(configPath);

        if (!subOptions?.length) {
            return { jsx: null, rowsUsed: 0 };
        }

        return {
            jsx: <DDJJSubOptions formId={FORM_ID} option={vKey} rowStart={startingRow} subOptions={subOptions} />,
            rowsUsed: subOptions.length,
        };
    };

    const generateInitialValues = () => {
        const initValues = {};

        ddjjStatements.forEach((vKey) => {
            const subOptions = configUtils.getArray(`${FORM_ID}.${vKey}.options`);
            if (!subOptions?.length) {
                return;
            }

            initValues[vKey] = {};

            subOptions.forEach((opt) => {
                const existingValues = ddjjGoods[vKey]?.[opt];
                initValues[vKey][opt] = existingValues ?? false;
            });
        });

        return initValues;
    };

    const saveComexRequest = (isContinue, values) => {
        const _comexTransferRequest = {
            ...comexTransfer,
            ddjjGoods: {
                ...values,
            },
            step: "stepGOODSDDJJ",
            comexId,
            isContinue,
        };
        dispatch(comexActions.createComexTransfer(_comexTransferRequest));
    };

    const handleSubmit = (values) => {
        dispatch(
            comexActions.setComexTransfer({
                ...comexTransfer,
                ddjjGoods: {
                    ...values,
                },
            }),
        );
        saveComexRequest(true, values);
        dispatch(push("/comex/ddjj/general"));
    };

    return (
        <>
            {isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={7} type="goods" />}
            <Notification scopeToShow="ComexNotification" />
            <ResponsiveHeader ticket metaTitle="transfers.comex.title" backLinkTo="/comex/goods/documentation" />

            <MainContainer showLoader={false}>
                <Notification scopeToShow="UploadNotification" />
                <Formik initialValues={generateInitialValues()} onSubmit={(values) => handleSubmit(values)}>
                    {({ values }) => {
                        setFormValues(values);

                        return (
                            <Form>
                                <GridTable className="form">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={10}
                                            rowStart={1}>
                                            <CustomText
                                                text="comex.tax.importPayment.title"
                                                textType="I18N"
                                                className="comex-title"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={10}
                                            rowStart={2}>
                                            <CustomText
                                                text="comex.ddjj.services.title"
                                                textType="I18N"
                                                className="comex-title"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={10}
                                            rowStart={3}>
                                            <CustomText
                                                text="comex.ddjj.services.statement.intro"
                                                textType="I18N"
                                                className="ddjj-item"
                                            />
                                        </GridTable.Data>

                                        {ddjjStatements.map((vKey) => {
                                            if (!enabledStatements[vKey]) {
                                                return null;
                                            }

                                            /* eslint-disable no-plusplus */
                                            const itemRow = currentRow.current++;
                                            const { jsx: subOptionsJSX, rowsUsed } = renderSubOptionsWithRows(
                                                vKey,
                                                currentRow.current,
                                            );
                                            currentRow.current += rowsUsed;

                                            return (
                                                <React.Fragment key={vKey}>
                                                    <DDJJItem formId={FORM_ID} vKey={vKey} row={itemRow} />
                                                    {subOptionsJSX}
                                                </React.Fragment>
                                            );
                                        })}
                                    </GridTable.Container>
                                </GridTable>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button type="submit" className="btn-primary" label="global.continue" />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

StepDDJJGoods.propTypes = {
    isDesktop: bool.isRequired,
};

export default StepDDJJGoods;
